import { Link } from "react-router-dom";
import { Linkedin, Github, Mail } from "lucide-react";

const XIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18" {...props}>
    <path d="M18.244 2H21.5l-7.5 8.57L22.5 22h-6.82l-5.34-6.96L4.2 22H.94l8.02-9.16L1.5 2h6.96l4.82 6.38L18.244 2Zm-2.39 18.2h1.81L7.23 3.72H5.3l10.553 16.48Z" />
  </svg>
);

const footerGroups = [
  {
    title: "Services",
    links: [
      { to: "/services#modernization-assurance", label: "Modernization Assurance" },
      { to: "/services#full-modernization", label: "Full Modernization" },
      { to: "/services#application-development", label: "Application Development" },
      { to: "/services#cloud", label: "Cloud Services" },
      { to: "/services#consulting", label: "IT Consulting & Staffing" },
      { to: "/services#devops", label: "DevOps & Automation" },
      { to: "/services#data-ai", label: "Data & AI Engineering" },
    ],
  },
  {
    title: "Products",
    links: [
      { to: "/products/legacy-testing-suite", label: "Legacy Testing Suite" },
      { to: "/products/ai-usage-analytics", label: "AI Usage Analytics" },
    ],
  },
  {
    title: "Company",
    links: [
      { to: "/about", label: "About" },
      { to: "/founder", label: "Founder" },
      { to: "/case-studies", label: "Case Studies" },
      { to: "/contact", label: "Contact" },
      { href: "https://blog.unitedtechlab.com", label: "Blog ↗" },
    ],
  },
  {
    title: "Legal",
    links: [
      { to: "/privacy", label: "Privacy Policy" },
      { to: "/terms", label: "Terms of Service" },
      { to: "/refund", label: "Refund Policy" },
      { to: "/cookies", label: "Cookie Policy" },
    ],
  },
];

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="relative z-[3] border-t border-white/10 bg-obsidian text-white/70"
    >
      <div className="bg-grid border-b border-white/5">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="chip text-neon-green">&gt; united_techlab.sys</div>
            <h3 className="font-display text-3xl font-bold text-white mt-3 leading-tight">
              Verification-first modernization.
              <br />
              Engineering-grade accountability.
            </h3>
            <p className="mt-5 text-sm leading-relaxed text-white/60 max-w-md">
              We help enterprises modernize mission-critical legacy systems
              without breaking business-critical behavior, and deliver the
              IT services and products that keep them ahead.
            </p>
            <div className="mt-6 flex items-center gap-3">
              <a
                data-testid="footer-social-linkedin"
                href="https://www.linkedin.com/in/jspeedluk/"
                target="_blank"
                rel="noreferrer"
                className="h-10 w-10 inline-flex items-center justify-center border border-white/10 hover:border-neon-green hover:text-neon-green transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin size={18} />
              </a>
              <a
                data-testid="footer-social-x"
                href="https://x.com/jspeedluk"
                target="_blank"
                rel="noreferrer"
                className="h-10 w-10 inline-flex items-center justify-center border border-white/10 hover:border-neon-green hover:text-neon-green transition-colors"
                aria-label="X"
              >
                <XIcon />
              </a>
              <a
                data-testid="footer-social-github"
                href="https://github.com/jspeedluk"
                target="_blank"
                rel="noreferrer"
                className="h-10 w-10 inline-flex items-center justify-center border border-white/10 hover:border-neon-green hover:text-neon-green transition-colors"
                aria-label="GitHub"
              >
                <Github size={18} />
              </a>
              <Link
                data-testid="footer-contact-link"
                to="/contact"
                className="h-10 w-10 inline-flex items-center justify-center border border-white/10 hover:border-neon-green hover:text-neon-green transition-colors"
                aria-label="Contact"
              >
                <Mail size={18} />
              </Link>
            </div>
          </div>

          <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-4 gap-8">
            {footerGroups.map((g) => (
              <div key={g.title}>
                <div className="chip text-white/50 mb-4">// {g.title}</div>
                <ul className="space-y-3">
                  {g.links.map((l) => (
                    <li key={l.label}>
                      {l.href ? (
                        <a
                          href={l.href}
                          target="_blank"
                          rel="noreferrer"
                          className="text-sm text-white/70 hover:text-neon-green transition-colors"
                        >
                          {l.label}
                        </a>
                      ) : (
                        <Link
                          to={l.to}
                          className="text-sm text-white/70 hover:text-neon-green transition-colors"
                        >
                          {l.label}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs font-mono text-white/40">
        <div>
          © {new Date().getFullYear()} United Techlab. All rights reserved.
        </div>
        <div className="flex items-center gap-3">
          <span className="h-1.5 w-1.5 rounded-full bg-neon-green animate-pulse" />
          <span>system.status: operational</span>
        </div>
      </div>
    </footer>
  );
}
